/**
 * Phase 7 Commit J. Enhance step — sticky summary on top, social-proof
 * line on top add-on, modifier groups with required tags. Action bar
 * (RunningBar) is rendered by App.tsx; this component is content-only.
 */

import type { BootstrapResponse } from '../types';
import { CheckIcon } from '../icons';
import {
  hasRequiredAddOns,
  isRequiredAddOnGateSatisfied,
} from '../lib/addon-gate';

type ServiceUpsells = NonNullable<BootstrapResponse['upsellsByService']>[string];

interface Props {
  upsells: ServiceUpsells;
  selectedAddOnIds: string[];
  selectedModifierIds: string[];
  onToggleAddOn: (
    serviceId: string,
    groupName: string | null,
    siblings: string[],
  ) => void;
  onToggleModifier: (
    modifierId: string,
    groupName: string | null,
    siblings: string[],
  ) => void;
  showPrices: boolean;
}

function formatDuration(m: number): string {
  // Empty string for 0/negative — see Service.tsx for the rationale.
  // Many add-on services (One Color/No Design, Tier 1/3/4 Design,
  // most waxing services) have durationMinutes=0 in Meevo because
  // they don't have a fixed time commitment.
  if (m <= 0) return '';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

export function EnhanceStep({
  upsells,
  selectedAddOnIds,
  selectedModifierIds,
  onToggleAddOn,
  onToggleModifier,
  showPrices,
}: Props) {
  // Group modifiers by groupName so the renderer can show a single-select
  // group together. `null` group means standalone (multi-select).
  const groups = new Map<string | null, ServiceUpsells['modifiers']>();
  for (const m of upsells.modifiers) {
    const key = m.groupName ?? null;
    const arr = groups.get(key) ?? [];
    arr.push(m);
    groups.set(key, arr);
  }

  // Group add-ons by groupName so a single-select group (e.g. Lotus's "Post
  // Color" finish) renders as radios; `null` = standalone multi-select checkbox.
  const addOnGroups = new Map<string | null, ServiceUpsells['addOns']>();
  for (const a of upsells.addOns) {
    const key = a.groupName ?? null;
    const arr = addOnGroups.get(key) ?? [];
    arr.push(a);
    addOnGroups.set(key, arr);
  }

  // Highlight the highest-attach add-on (mocked here as the first one,
  // since we don't yet surface attach-rate from the API). Renders as
  // "Most-added" copy beside the meta line. Only meaningful for standalone
  // add-ons — a required single-select group shouldn't nudge a pick.
  const mostAddedId = (addOnGroups.get(null) ?? [])[0]?.serviceId ?? null;

  // Required-add-on gate. When the base service has any add-on marked
  // isRequired (Meevo's numberOfRequiredAddons ≥ 1), the user must
  // select at least one of the required ones before Continue is enabled
  // (App.tsx passes the same predicate to RunningBar.disabled).
  // Subtitle + banner reflect the requirement so the UX matches Meevo's
  // native widget — a customer booking Builder Gel Fill cannot silently
  // skip a design-tier selection. Previously the hardcoded "Optional"
  // subtitle lied when required add-ons were configured, leading to
  // underspecified bookings at the salon.
  const hasRequired = hasRequiredAddOns(upsells.addOns);
  const meetsRequired = isRequiredAddOnGateSatisfied({
    addOns: upsells.addOns,
    selectedAddOnIds,
  });

  return (
    <div class="sbw-scroll">
      <div class="sbw-eyebrow">Step 2 — make it yours</div>
      <h2 class="sbw-step-title">A few finishing touches?</h2>
      <p class="sbw-step-sub">
        {!hasRequired
          ? "Optional add-ons to round out your service. Skip any that aren't your thing."
          : meetsRequired
            ? 'Add more if you like, or continue below.'
            : 'Pick at least one option marked Required to continue.'}
      </p>

      {hasRequired && !meetsRequired && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--sbw-required-bg, #FEF2F2)',
            color: 'var(--sbw-required-fg, #B91C1C)',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Please select a required add-on
        </div>
      )}

      {Array.from(addOnGroups.entries()).map(([groupName, members]) => {
        const isGroup = groupName !== null;
        const groupRequired = members.some((m) => m.isRequired);
        const siblingIds = members.map((m) => m.serviceId);

        const list = (
          <div class="sbw-enhance-list">
            {members.map((a) => {
              const checked = selectedAddOnIds.includes(a.serviceId);
              const isMostAdded = !isGroup && a.serviceId === mostAddedId;
              const toggle = () =>
                onToggleAddOn(a.serviceId, groupName, siblingIds);
              return (
                <div
                  key={a.serviceId}
                  class={'sbw-enhance' + (checked ? ' is-on' : '')}
                  onClick={toggle}
                  role={isGroup ? 'radio' : 'checkbox'}
                  aria-checked={checked}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle();
                    }
                  }}
                >
                  <span
                    class="sbw-checkbox"
                    style={isGroup ? { borderRadius: '50%' } : undefined}
                  >
                    {checked && <CheckIcon size={12} />}
                  </span>
                  <div class="sbw-enhance-body">
                    <div class="sbw-enhance-name">
                      {a.name}
                      {/* Standalone required add-ons keep a per-row tag; a group
                          carries a single "Required" tag on its header instead. */}
                      {!isGroup && a.isRequired && (
                        <span class="sbw-required-tag" style={{ marginLeft: 8 }}>
                          Required
                        </span>
                      )}
                    </div>
                    {(formatDuration(a.durationMinutes) || isMostAdded) && (
                      <div class="sbw-enhance-meta">
                        {formatDuration(a.durationMinutes) &&
                          `+${formatDuration(a.durationMinutes)}`}
                        {isMostAdded && (
                          <span
                            style={{
                              color: 'var(--sbw-brand-fg)',
                              fontWeight: 600,
                              marginLeft: 6,
                            }}
                          >
                            {formatDuration(a.durationMinutes) ? '· ' : ''}Most-added
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {showPrices && (
                    <div class="sbw-enhance-delta">
                      {a.priceCents === 0
                        ? 'Included'
                        : `+$${Math.round(a.priceCents / 100)}`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

        if (!isGroup) return <div key="__addons__">{list}</div>;
        return (
          <div key={groupName} class="sbw-modgroup">
            <div class="sbw-modgroup-h">
              <h4>{groupName}</h4>
              {groupRequired && <span class="sbw-required-tag">Required</span>}
            </div>
            {list}
          </div>
        );
      })}

      {Array.from(groups.entries()).map(([groupName, members]) => {
        const isGroup = groupName !== null;
        const groupRequired = members.some((m) => m.isRequired);
        const siblingIds = members.map((m) => m.id);

        if (!isGroup) {
          // Ungrouped modifiers render as multi-select toggles, same shape
          // as add-ons.
          return (
            <div key="__ungrouped__" class="sbw-modgroup">
              <div class="sbw-modgroup-h">
                <h4>Customize</h4>
              </div>
              <div class="sbw-enhance-list">
                {members.map((m) => {
                  const checked = selectedModifierIds.includes(m.id);
                  return (
                    <div
                      key={m.id}
                      class={'sbw-enhance' + (checked ? ' is-on' : '')}
                      onClick={() => onToggleModifier(m.id, null, siblingIds)}
                      role="checkbox"
                      aria-checked={checked}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onToggleModifier(m.id, null, siblingIds);
                        }
                      }}
                    >
                      <span class="sbw-checkbox">
                        {checked && <CheckIcon size={12} />}
                      </span>
                      <div class="sbw-enhance-body">
                        <div class="sbw-enhance-name">
                          {m.name}
                          {m.isRequired && (
                            <span class="sbw-required-tag" style={{ marginLeft: 8 }}>
                              Required
                            </span>
                          )}
                        </div>
                      </div>
                      <div class="sbw-enhance-delta">
                        {m.priceCents === 0
                          ? 'Included'
                          : `+$${Math.round(m.priceCents / 100)}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        return (
          <div key={groupName} class="sbw-modgroup">
            <div class="sbw-modgroup-h">
              <h4>{groupName}</h4>
              {groupRequired && <span class="sbw-required-tag">Required</span>}
            </div>
            <div class="sbw-enhance-list">
              {members.map((m) => {
                const checked = selectedModifierIds.includes(m.id);
                return (
                  <div
                    key={m.id}
                    class={'sbw-enhance' + (checked ? ' is-on' : '')}
                    onClick={() =>
                      onToggleModifier(m.id, groupName, siblingIds)
                    }
                    role="radio"
                    aria-checked={checked}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggleModifier(m.id, groupName, siblingIds);
                      }
                    }}
                  >
                    <span
                      class="sbw-checkbox"
                      style={{ borderRadius: '50%' }}
                    >
                      {checked && <CheckIcon size={12} />}
                    </span>
                    <div class="sbw-enhance-body">
                      <div class="sbw-enhance-name">{m.name}</div>
                    </div>
                    <div class="sbw-enhance-delta">
                      {m.priceCents === 0
                        ? 'Included'
                        : `+$${Math.round(m.priceCents / 100)}`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
